import React, { useState, useEffect, Component, useMemo, useCallback, useRef } from 'react';
import { 
    Calculator, CreditCard, Wallet, X, ShieldAlert,
    TrendingUp, PiggyBank, RefreshCcw, Landmark, Zap, 
    Scale, Lock, Unlock, Plus, Trash2, Save, RotateCcw, CheckCircle2,
    ChevronRight, Coffee, Calendar, Info, Send, Edit3, AlertTriangle,
    Loader2, Check, Sparkles, WifiOff
} from 'lucide-react';
import * as XLSX from 'xlsx';

// Import fungsi Firebase
import { saveRekapData, db, getAgents, WEBHOOK_ID } from './firebase'; 
import { doc, onSnapshot, getDoc, deleteDoc } from "firebase/firestore";

const formatIDR = (val) => {
    const number = parseFloat(val);
    if (isNaN(number) || number === 0) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(number);
};

const parseRawNumber = (val) => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    const cleaned = val.toString().replace(/\D/g, '');
    return cleaned ? parseInt(cleaned, 10) : 0;
};

// Parser Regex khusus untuk Saldo Akhir NEWBIEZ
const parseNewbiezBalance = (val) => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    const regex = /=\s*([\d.]+)\s*@/;
    const match = val.toString().match(regex);
    if (match && match[1]) {
        const cleaned = match[1].replace(/\./g, '');
        return parseInt(cleaned, 10) || 0;
    }
    return parseRawNumber(val);
};

class ErrorBoundary extends Component {
    constructor(props) { super(props); this.state = { hasError: false }; }
    static getDerivedStateFromError(error) { return { hasError: true }; }
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 text-center">
                    <div className="bg-white p-10 rounded-[3rem] shadow-xl max-w-md border border-red-100">
                        <div className="bg-red-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-red-600"><ShieldAlert size={40} /></div>
                        <h2 className="text-2xl font-black text-slate-800 mb-2">Terjadi Kesalahan</h2>
                        <button onClick={() => window.location.reload()} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold flex items-center justify-center gap-2"><RefreshCcw size={18} /> Muat Ulang</button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

// ============================================================
// LOADING UI (ringan: hanya CSS transform/opacity, tanpa library)
// ============================================================
const LOADING_CSS = `
@keyframes rk-indeterminate { 0% { transform: translateX(-100%); } 100% { transform: translateX(350%); } }
@keyframes rk-dot { 0%, 80%, 100% { transform: scale(.6); opacity: .4; } 40% { transform: scale(1); opacity: 1; } }
.rk-bar { animation: rk-indeterminate 1.2s ease-in-out infinite; will-change: transform; }
.rk-dot { animation: rk-dot 1.2s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .rk-bar, .rk-dot { animation: none; } }
`;

const TopProgressBar = ({ active }) => {
    if (!active) return null;
    return (
        <div className="fixed top-0 left-0 right-0 h-1 z-[200] overflow-hidden bg-blue-100" role="progressbar" aria-label="Memuat">
            <div className="rk-bar h-full w-1/3 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600 rounded-full" />
        </div>
    );
};

const BouncingDots = () => (
    <span className="inline-flex items-center gap-1 ml-1" aria-hidden="true">
        {[0, 1, 2].map(i => (
            <span key={i} className="rk-dot w-1.5 h-1.5 rounded-full bg-blue-600" style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
    </span>
);

const SkeletonBlock = ({ className = '' }) => (
    <div className={`bg-slate-200/70 rounded-2xl animate-pulse ${className}`} />
);

// Ditampilkan menggantikan bagian bawah halaman selama data pertama belum siap,
// sementara header & kartu statistik tetap langsung terlihat (progressive loading).
const LoadingPanel = ({ error, onRetry, stage }) => {
    if (error) {
        return (
            <div className="w-full mb-8 bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-red-100 text-center" role="alert">
                <div className="bg-red-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 text-red-600">
                    <WifiOff size={30} />
                </div>
                <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight mb-2">Gagal Memuat Data</h3>
                <p className="text-xs font-bold text-slate-500 mb-6 max-w-md mx-auto leading-relaxed">{error}</p>
                <button onClick={onRetry} className="px-8 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg active:scale-95 transition-all inline-flex items-center gap-2">
                    <RefreshCcw size={16} /> Coba Lagi
                </button>
            </div>
        );
    }
    return (
        <div className="w-full mb-8" aria-busy="true" aria-live="polite">
            <div className="bg-white p-6 md:p-8 rounded-[2.5rem] shadow-sm border border-slate-100 mb-6">
                <div className="flex flex-col items-center text-center py-4 md:py-6">
                    <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-3xl flex items-center justify-center mb-4 shadow-inner">
                        <Loader2 size={32} className="animate-spin" />
                    </div>
                    <h3 className="text-sm md:text-base font-black text-slate-800 uppercase tracking-tight flex items-center">
                        Memuat Data<BouncingDots />
                    </h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{stage || 'Mohon Tunggu'}</p>
                    <div className="w-full max-w-xs h-1.5 bg-slate-100 rounded-full overflow-hidden mt-5">
                        <div className="rk-bar h-full w-1/3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full" />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-4">
                    {[0, 1, 2, 3, 4].map(i => <SkeletonBlock key={i} className="h-16" />)}
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SkeletonBlock className="h-40 rounded-[2rem]" />
                <SkeletonBlock className="h-40 rounded-[2rem]" />
            </div>
        </div>
    );
};

const StableInput = ({ id, label, icon, color, bg, border, disabled, value, onChange, className = "", textColor = "text-slate-700" }) => {
    const [displayValue, setDisplayValue] = useState(formatIDR(value));
    useEffect(() => { setDisplayValue(formatIDR(value)); }, [value]);
    const handleChange = (e) => {
        const raw = e.target.value;
        const numValue = parseRawNumber(raw);
        setDisplayValue(formatIDR(numValue));
        onChange(id, numValue);
    };
    return (
        <div className={`space-y-2 ${className}`}>
            {label && <label className={`text-[10px] font-black ${color} ml-2 uppercase flex items-center gap-1`}>{icon} {label}</label>}
            <input 
                type="text" 
                inputMode="numeric" 
                value={displayValue} 
                onChange={handleChange} 
                disabled={disabled} 
                className={`w-full p-4 ${bg} rounded-2xl border-2 border-transparent ${border} focus:bg-white transition-all font-bold outline-none ${textColor} disabled:opacity-60 disabled:cursor-not-allowed`} 
                placeholder="Rp 0" 
            />
        </div>
    );
};

const RekapanDashboard = ({ externalBalances, totalProfit, yesterdayBalances, supplierDailyUsage, filterDate, bankUsage, webhookId, dailyStats, dailyData }) => {
    const ID_SISTEM = webhookId || WEBHOOK_ID; 
    const TELEGRAM_BOT_TOKEN = "8571888481:AAGdqdiaa4kPd_YWqfZauELZ1-ACiMV0iYg";
    const TELEGRAM_CHAT_ID = "-1003574594574";

    const [isDiffModalOpen, setIsDiffModalOpen] = useState(false);
    const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
    const [showLockConfirm, setShowLockConfirm] = useState(false);
    
    const [isModalLock, setIsModalLock] = useState(true);
    const [isInvestasiLock, setIsInvestasiLock] = useState(true);
    const [isOpsLock, setIsOpsLock] = useState(true); 
    const [isSupplierEdit, setIsSupplierEdit] = useState(false);
    
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [isDayLocked, setIsDayLocked] = useState(false);
    const [syncStatus, setSyncStatus] = useState('');
    const [profitIsLocked, setProfitIsLocked] = useState(false);

    // State loading nyata (mengikuti proses sebenarnya)
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [retryKey, setRetryKey] = useState(0);
    const [agentsLoading, setAgentsLoading] = useState(true);
    const prevDocCache = useRef(new Map()); // cache dokumen hari sebelumnya, hindari getDoc berulang
    const loadedDateRef = useRef(null);     // tanggal yang sudah pernah selesai dimuat

    // State untuk Animasi & Loading Kirim Telegram Modern
    const [isSendingTelegram, setIsSendingTelegram] = useState(false);
    const [sendStage, setSendStage] = useState('idle'); // 'idle' | 'excel' | 'supplier' | 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState('');

    const [agents, setAgents] = useState([]);
    const [tempOpsName, setTempOpsName] = useState('');
    const [tempOpsValue, setTempOpsValue] = useState(0);
    const [labaBersihKemarin, setLabaBersihKemarin] = useState(0);
    const [yesterdayData, setYesterdayData] = useState(null);

    const emptyData = {
        modalUtama: 0,
        investasi: 0,
        saldoBCA: 0, 
        saldoBRI: 0, 
        settledDigiflazz: 0, 
        settledTargetBank: 'BCA', // Pilihan default target bank untuk Settled Digiflazz
        saldoMidtrans: 0, 
        sisaDigiflazz: 0,
        saldoDigiswitch: 0, 
        biayaOpsList: [], 
        catatanAnalisisLainnya: '',
        profitCloudSistem: 0, 
        lockedSupplierBalances: null,
        manualSupplierAdjustments: {},
        confirmedTopups: {}, 
        supplierSources: {}, // Pemetaan Sumber Dana Penambahan Supplier: { Digiflazz: 'BCA', DAS: 'BRI', ... }
        manualDifference: null,
        dailyStatsSnapshot: null
    };

    const [rekapData, setRekapData] = useState(emptyData);

    useEffect(() => {
        let cancelled = false;
        const fetchAgents = async () => {
            setAgentsLoading(true);
            try {
                const data = await getAgents();
                if (!cancelled) setAgents(data);
            } catch (err) {
                console.error("Gagal sinkron data agen:", err);
            } finally {
                if (!cancelled) setAgentsLoading(false);
            }
        };
        fetchAgents();
        return () => { cancelled = true; };
    }, [retryKey]);

    const effectiveDate = selectedDate || filterDate || new Date().toISOString().split('T')[0];

    useEffect(() => {
        let cancelled = false;
        const isNewDate = loadedDateRef.current !== effectiveDate;
        if (isNewDate) {
            setIsInitialLoading(true);
            setSyncStatus('Memuat data...');
        }
        setLoadError('');

        // Timeout: jika server tidak merespons, tampilkan error + tombol coba lagi
        const timeoutId = setTimeout(() => {
            if (!cancelled && loadedDateRef.current !== effectiveDate) {
                setLoadError('Server tidak merespons. Periksa koneksi internet Anda lalu coba lagi.');
                setSyncStatus('Gagal Terhubung');
            }
        }, 15000);

        const docRef = doc(db, "rekapan_operasional", effectiveDate);
        
        const unsubscribe = onSnapshot(docRef, async (docSnap) => {
            const prevDate = new Date(effectiveDate);
            prevDate.setDate(prevDate.getDate() - 1);
            const prevDateStr = prevDate.toISOString().split('T')[0];

            // Ambil dokumen kemarin dengan cache singkat (30 dtk) agar tidak request berulang
            let prevSnap;
            const cached = prevDocCache.current.get(prevDateStr);
            if (cached && Date.now() - cached.at < 30000) {
                prevSnap = cached.snap;
            } else {
                try {
                    prevSnap = await getDoc(doc(db, "rekapan_operasional", prevDateStr));
                    prevDocCache.current.set(prevDateStr, { snap: prevSnap, at: Date.now() });
                } catch (err) {
                    if (cancelled) return;
                    console.error("Gagal memuat data kemarin:", err);
                    clearTimeout(timeoutId);
                    setLoadError('Gagal mengambil data dari server. Periksa koneksi internet Anda lalu coba lagi.');
                    setSyncStatus('Gagal Terhubung');
                    return;
                }
            }
            if (cancelled) return;
            
            let prevModalUtama = 0;
            let prevInvestasi = 0;

            if (prevSnap.exists()) {
                const pData = prevSnap.data();
                setYesterdayData(pData);
                setLabaBersihKemarin(pData.labaBersihCalculated || 0);
                prevModalUtama = pData.modalUtama || 0;
                prevInvestasi = pData.investasi || 0;
            } else {
                setYesterdayData(null);
                setLabaBersihKemarin(0);
            }

            const currentDayData = dailyData?.[effectiveDate];
            const currentDayProfit = currentDayData ? (currentDayData.profit || 0) : (parseFloat(totalProfit) || 0);

            if (docSnap.exists()) {
                const cloudData = docSnap.data();
                const hasSavedProfit = cloudData.profitCloudSistem !== undefined && cloudData.profitCloudSistem !== 0;
                
                setRekapData(prev => ({
                    ...emptyData,
                    ...cloudData,
                    modalUtama: cloudData.modalUtama ?? prevModalUtama,
                    investasi: cloudData.investasi ?? prevInvestasi,
                    settledTargetBank: cloudData.settledTargetBank || 'BCA',
                    supplierSources: cloudData.supplierSources || {},
                    profitCloudSistem: hasSavedProfit ? cloudData.profitCloudSistem : currentDayProfit
                }));
                setProfitIsLocked(hasSavedProfit);
                setIsDayLocked(cloudData.isLocked || false);
                setSyncStatus('Live');
            } else {
                setSyncStatus('Memeriksa riwayat...');
                if (prevSnap.exists()) {
                    const lastData = prevSnap.data();
                    setRekapData({ 
                        ...emptyData, 
                        modalUtama: lastData.modalUtama || 0, 
                        investasi: lastData.investasi || 0,
                        biayaOpsList: lastData.biayaOpsList || [],
                        catatanAnalisisLainnya: lastData.catatanAnalisisLainnya || '',
                        settledTargetBank: 'BCA',
                        supplierSources: {},
                        profitCloudSistem: currentDayProfit
                    });
                } else {
                    setRekapData(prev => ({ 
                        ...emptyData, 
                        modalUtama: prev.modalUtama || prevModalUtama,
                        investasi: prev.investasi || prevInvestasi,
                        settledTargetBank: 'BCA',
                        supplierSources: {},
                        profitCloudSistem: currentDayProfit 
                    }));
                }
                setProfitIsLocked(false);
                setIsDayLocked(false);
                setSyncStatus('Ready');
            }

            // Data siap -> sembunyikan loading, tampilkan konten
            clearTimeout(timeoutId);
            loadedDateRef.current = effectiveDate;
            setLoadError('');
            setIsInitialLoading(false);
        }, (err) => {
            if (cancelled) return;
            console.error("Gagal listener Firestore:", err);
            clearTimeout(timeoutId);
            setLoadError('Koneksi ke server terputus atau akses ditolak. Periksa koneksi internet Anda lalu coba lagi.');
            setSyncStatus('Gagal Terhubung');
        });

        return () => { cancelled = true; clearTimeout(timeoutId); unsubscribe(); };
    }, [effectiveDate, totalProfit, dailyData, retryKey]);

    const handleRetryLoad = useCallback(() => {
        prevDocCache.current.clear();
        loadedDateRef.current = null;
        setLoadError('');
        setIsInitialLoading(true);
        setSyncStatus('Memuat data...');
        setRetryKey(k => k + 1);
    }, []);


    const handleResetSelectedDate = async () => {
        if (isDayLocked) {
            alert("Tanggal ini terkunci! Harap buka kunci terlebih dahulu.");
            return;
        }

        if (window.confirm(`Yakin ingin mereset & menghapus data rekapan hanya pada tanggal (${effectiveDate})?`)) {
            setSyncStatus('Resetting Selected Date...');
            try {
                await deleteDoc(doc(db, "rekapan_operasional", effectiveDate));
                
                const preservedModalUtama = rekapData.modalUtama || yesterdayData?.modalUtama || 0;
                const preservedInvestasi = rekapData.investasi || yesterdayData?.investasi || 0;

                setRekapData({ 
                    ...emptyData,
                    modalUtama: preservedModalUtama,
                    investasi: preservedInvestasi
                });

                setProfitIsLocked(false);
                setIsDayLocked(false);
                setSyncStatus('Date Reset Complete');
                alert(`Data rekapan tanggal ${effectiveDate} berhasil direset! (Modal Utama & Investasi tetap dipertahankan)`);
                setTimeout(() => setSyncStatus('Ready'), 2000);
            } catch (error) {
                console.error("Gagal mereset data tanggal terpilih:", error);
                setSyncStatus('Reset Failed');
                alert("Gagal mereset data pada Firestore: " + error.message);
            }
        }
    };

    const activeStats = useMemo(() => {
        if (isDayLocked && rekapData.dailyStatsSnapshot) {
            return rekapData.dailyStatsSnapshot;
        }

        const currentDayData = dailyData?.[effectiveDate];
        if (currentDayData) {
            const sales = currentDayData.jual || currentDayData.totalSales || 0;
            const cost = currentDayData.modal || currentDayData.totalCost || 0;
            const successTrx = currentDayData.trxSukses || currentDayData.successTrx || 0;
            const failedTrx = currentDayData.trxGagal || currentDayData.failedTrx || 0;
            const totalTrx = currentDayData.totalTrx || (successTrx + failedTrx);
            const calculatedSuccessRate = totalTrx > 0 ? ((successTrx / totalTrx) * 100).toFixed(1) : 0;

            return {
                totalTrx: totalTrx,
                successTrx: successTrx,
                pendingTrx: currentDayData.pendingTrx || 0,
                failedTrx: failedTrx,
                successRate: currentDayData.successRate || calculatedSuccessRate,
                totalSales: sales,
                totalCost: cost
            };
        }

        if (dailyStats) {
            return dailyStats;
        }

        return { 
            totalTrx: 0, successTrx: 0, pendingTrx: 0, failedTrx: 0, 
            successRate: 0, totalSales: 0, totalCost: 0 
        };
    }, [isDayLocked, rekapData.dailyStatsSnapshot, dailyData, effectiveDate, dailyStats]);

    // Pemrosesan Saldo Supplier Riil & Penambahan
    // ============================================================
    // FIX: Deteksi Penambahan (topup) hanya dihitung jika:
    //  1. Ada snapshot terkunci dari hari kemarin (yesterdayData.lockedSupplierBalances)
    //  2. Nilai 'kemarin' > 0 (bukan default/no-data)
    //  3. Hasil diff benar-benar positif
    // Fallback ke `yesterdayBalances` prop TIDAK dipercaya untuk deteksi topup
    // karena bisa saja berisi saldo live (sumber bug "selalu ada penambahan").
    // ============================================================
    const displaySupplierBalances = useMemo(() => {
        const isSnapshotLocked = !!rekapData.lockedSupplierBalances;
        const getSupData = (name) => {
            const manualVal = rekapData.manualSupplierAdjustments?.[name];
            let baseData;

            if (isSnapshotLocked) {
                const snapshot = rekapData.lockedSupplierBalances[name];
                baseData = typeof snapshot === 'object' 
                    ? { ...snapshot, hasValidKemarin: true } 
                    : { real: snapshot || 0, kemarin: 0, pakai: 0, hasValidKemarin: false };
            } else {
                let realExt = 0;
                if (name === 'Digiflazz') {
                    realExt = parseRawNumber(rekapData.sisaDigiflazz);
                } else {
                    const rawExt = externalBalances?.[name];
                    realExt = name === 'NEWBIEZ' ? parseNewbiezBalance(rawExt) : parseRawNumber(rawExt);
                }

                // === SUMBER 'KEMARIN' YANG VALID ===
                // Prioritas utama: snapshot terkunci dari hari kemarin.
                // Jangan gunakan `yesterdayBalances` sebagai sumber topup karena
                // prop ini tidak bisa dijamin berisi data snapshot kemarin yang asli.
                const lockedSnapshot = yesterdayData?.lockedSupplierBalances?.[name];
                let kemarinVal = 0;
                let hasValidKemarin = false;

                if (lockedSnapshot !== undefined && lockedSnapshot !== null) {
                    kemarinVal = typeof lockedSnapshot === 'object' 
                        ? (lockedSnapshot.real ?? 0) 
                        : lockedSnapshot;
                    // Hanya valid jika > 0. Nilai 0 berarti tidak ada data atau saldo benar-benar kosong
                    // (tidak bisa dipakai sebagai basis perhitungan topup).
                    hasValidKemarin = kemarinVal > 0;
                } else if (yesterdayBalances && yesterdayBalances[name] !== undefined && yesterdayBalances[name] !== null) {
                    // Fallback display saja. Tidak menandai sebagai valid untuk deteksi topup
                    // guna mencegah false positive saat data live di-pass sebagai yesterdayBalances.
                    kemarinVal = parseRawNumber(yesterdayBalances[name]);
                    hasValidKemarin = false;
                }

                // === PEMAKAIAN (pakai) ===
                // Hanya dari data usage harian aktual. Tidak ada fallback estimasi
                // agar nominal yang ditampilkan dapat ditelusuri sumbernya.
                let pakaiVal = 0;
                if (supplierDailyUsage?.[effectiveDate]?.[name] !== undefined) {
                    pakaiVal = supplierDailyUsage[effectiveDate][name];
                } else if (supplierDailyUsage?.[filterDate]?.[name] !== undefined) {
                    pakaiVal = supplierDailyUsage[filterDate][name];
                }

                baseData = {
                    real: realExt,
                    kemarin: kemarinVal,
                    pakai: pakaiVal,
                    hasValidKemarin
                };
            }

            const realFinal = manualVal !== undefined ? manualVal : baseData.real;
            const expected = baseData.kemarin - baseData.pakai;
            const diff = realFinal - expected;

            // === FIX UTAMA ===
            // Topup hanya dianggap ada jika kita punya basis data kemarin yang valid (> 0).
            // Tanpa basis valid, diff positif bisa muncul karena alasan lain (data live vs snapshot,
            // reload halaman, sinkronisasi, dsb) — bukan karena transaksi penambahan.
            const tambah = (baseData.hasValidKemarin && baseData.kemarin > 0 && diff > 0) ? diff : 0;

            const selectedSource = rekapData.supplierSources?.[name] || 'BCA';

            return {
                real: realFinal,
                kemarin: baseData.kemarin,
                pakai: baseData.pakai,
                tambah: tambah,
                sourceBank: selectedSource,
                hasValidKemarin: baseData.hasValidKemarin,
                // isJanggal hanya muncul saat ada basis valid, agar tidak false alarm
                isJanggal: !rekapData.confirmedTopups?.[name] 
                    && baseData.hasValidKemarin 
                    && baseData.kemarin > 0 
                    && (realFinal !== expected)
            };
        };

        const suppliersList = ['DAS', 'AMC', 'HAO', 'NEWBIEZ', 'OASIS', 'ANTUM'];
        const suppliers = {};
        
        suppliers['Digiflazz'] = getSupData('Digiflazz');
        suppliersList.forEach(name => {
            suppliers[name] = getSupData(name);
        });

        suppliers.isLocked = isSnapshotLocked;

        let totalKemarin = 0;
        let totalPakai = 0;
        let totalReal = 0;
        let anyJanggal = false;
        let totalTambah = 0;
        let tambahBCA = 0;
        let tambahBRI = 0;

        ['Digiflazz', ...suppliersList].forEach(name => {
            const s = suppliers[name];
            totalKemarin += s.kemarin;
            totalPakai += s.pakai;
            totalReal += s.real;
            totalTambah += s.tambah;
            if (s.tambah > 0) {
                if (s.sourceBank === 'BRI') tambahBRI += s.tambah;
                else tambahBCA += s.tambah;
            }
            if (s.isJanggal) anyJanggal = true;
        });

        return {
            ...suppliers,
            aggregate: {
                kemarin: totalKemarin,
                pakai: totalPakai,
                tambah: totalTambah,
                tambahBCA,
                tambahBRI,
                isAnyJanggal: anyJanggal
            }
        };
    }, [rekapData.lockedSupplierBalances, rekapData.manualSupplierAdjustments, rekapData.sisaDigiflazz, rekapData.supplierSources, rekapData.confirmedTopups, externalBalances, yesterdayBalances, yesterdayData, supplierDailyUsage, effectiveDate, filterDate, activeStats]);

    // 1. Ketentuan Total Penambahan Aset BCA & BRI dari Settled Hari Sebelumnya dengan Pengurangan Wajib 2.775
    // Rumus: Total Settled Digiflazz Hari Kemarin - 2.775 = Total Penambahan BCA/BRI
    const assetBCA = useMemo(() => {
        const saldoKemarin = parseRawNumber(yesterdayBalances?.saldoBCA ?? yesterdayData?.saldoBCA);
        const saldoAktual = parseRawNumber(rekapData.saldoBCA);
        
        // Penambahan BCA diambil dari Total Settled Hari Sebelumnya jika target kemarin BCA
        const targetBankKemarin = yesterdayData?.settledTargetBank || 'BCA';
        const rawSettledKemarin = parseRawNumber(yesterdayData?.settledDigiflazz);
        const settledKemarin = (targetBankKemarin === 'BCA' && rawSettledKemarin > 0) 
            ? Math.max(0, rawSettledKemarin - 2775) 
            : 0;
            
        const totalPenambahan = settledKemarin;

        // Saldo Kemarin + Saldo Penambahan - Saldo Sekarang = Saldo Pengurangan
        const totalPengurangan = Math.max(0, saldoKemarin + totalPenambahan - saldoAktual);
        
        // Penambahan Supplier yang bersumber dari BCA
        const totalPenambahanSupplierBCA = displaySupplierBalances.aggregate.tambahBCA;

        const selisih = totalPenambahanSupplierBCA - totalPengurangan;
        const isNormal = selisih === 0;

        return {
            saldoKemarin,
            totalPenambahan,
            saldoAktual,
            totalPengurangan,
            penambahanSupplier: totalPenambahanSupplierBCA,
            selisih,
            isNormal
        };
    }, [yesterdayBalances, yesterdayData, rekapData.saldoBCA, displaySupplierBalances.aggregate.tambahBCA]);

    const assetBRI = useMemo(() => {
        const saldoKemarin = parseRawNumber(yesterdayBalances?.saldoBRI ?? yesterdayData?.saldoBRI);
        const saldoAktual = parseRawNumber(rekapData.saldoBRI);
        
        // Penambahan BRI diambil dari Total Settled Hari Sebelumnya jika target kemarin BRI
        const targetBankKemarin = yesterdayData?.settledTargetBank || 'BCA';
        const rawSettledKemarin = parseRawNumber(yesterdayData?.settledDigiflazz);
        const settledKemarin = (targetBankKemarin === 'BRI' && rawSettledKemarin > 0) 
            ? Math.max(0, rawSettledKemarin - 2775) 
            : 0;
            
        const totalPenambahan = settledKemarin;

        // Saldo Kemarin + Saldo Penambahan - Saldo Sekarang = Saldo Pengurangan
        const totalPengurangan = Math.max(0, saldoKemarin + totalPenambahan - saldoAktual);
        
        // Penambahan Supplier yang bersumber dari BRI
        const totalPenambahanSupplierBRI = displaySupplierBalances.aggregate.tambahBRI;

        const selisih = totalPenambahanSupplierBRI - totalPengurangan;
        const isNormal = selisih === 0;

        return {
            saldoKemarin,
            totalPenambahan,
            saldoAktual,
            totalPengurangan,
            penambahanSupplier: totalPenambahanSupplierBRI,
            selisih,
            isNormal
        };
    }, [yesterdayBalances, yesterdayData, rekapData.saldoBRI, displaySupplierBalances.aggregate.tambahBRI]);

    const results = useMemo(() => {
        const totalSaldoAgen = agents
            .filter(a => {
                const n = a.name.toLowerCase();
                return !n.includes('owner') && !n.includes('digiflazz');
            })
            .reduce((sum, a) => sum + (parseInt(a.balance) || 0), 0);

        const totalBankKotor = (rekapData.saldoBCA || 0) + (rekapData.saldoBRI || 0);
        const totalBankReal = totalBankKotor - totalSaldoAgen;
        
        const dayOfMonth = new Date(effectiveDate).getDate();
        const POTONGAN_PER_HARI = 2775;
        const totalPotonganAccumulated = dayOfMonth * POTONGAN_PER_HARI;
        
        const settledNet = rekapData.settledDigiflazz > 0 ? (rekapData.settledDigiflazz - POTONGAN_PER_HARI) : 0;
        const midtransNet = (rekapData.saldoMidtrans || 0);
        
        const currentSupplierSum = (rekapData.sisaDigiflazz || 0) + 
                                    displaySupplierBalances.DAS.real + 
                                    displaySupplierBalances.AMC.real + 
                                    displaySupplierBalances.HAO.real +
                                    displaySupplierBalances.NEWBIEZ.real +
                                    displaySupplierBalances.OASIS.real +
                                    displaySupplierBalances.ANTUM.real;

        const currentTotalAsset = totalBankReal + currentSupplierSum + settledNet + midtransNet;

        const labaBersih = currentTotalAsset - (rekapData.modalUtama || 0);

        const dayProfitData = dailyData?.[effectiveDate]?.profit;
        const fallbackProfit = dayProfitData !== undefined ? dayProfitData : parseFloat(totalProfit) || 0;
        const profitBasis = (rekapData.profitCloudSistem || fallbackProfit);

        const calculatedDiff = (labaBersih - profitBasis) + totalPotonganAccumulated;

        const finalSelisih = (rekapData.manualDifference !== null && rekapData.manualDifference !== undefined) 
                            ? rekapData.manualDifference 
                            : calculatedDiff;

        const profitKotorVal = (activeStats.totalSales || 0) - (activeStats.totalCost || 0);

        return { 
            totalSisaSaldo: currentTotalAsset, 
            labaBersih, 
            totalSupplierBalance: currentSupplierSum, 
            settledNetValue: settledNet, 
            midtransValue: midtransNet,
            selisihProfit: finalSelisih, 
            profitBasis,
            totalPotonganAccumulated,
            totalBankReal,
            totalSaldoAgen,
            isManualDiff: rekapData.manualDifference !== null,
            profitKotor: profitKotorVal
        };
    }, [rekapData, displaySupplierBalances, effectiveDate, agents, totalProfit, activeStats, dailyData]);

    const handleManualChange = useCallback((field, value) => {
        if (isDayLocked) return; 
        setRekapData(prev => ({ ...prev, [field]: value }));
    }, [isDayLocked]);

    const handleSupplierManualEdit = (name, value) => {
        if (name === 'Digiflazz') {
            setRekapData(prev => ({
                ...prev,
                sisaDigiflazz: value,
                manualSupplierAdjustments: {
                    ...(prev.manualSupplierAdjustments || {}),
                    [name]: value
                }
            }));
        } else {
            setRekapData(prev => ({
                ...prev,
                manualSupplierAdjustments: {
                    ...(prev.manualSupplierAdjustments || {}),
                    [name]: value
                }
            }));
        }
    };

    // Pilihan Sumber Dana Penambahan Saldo Supplier (BCA / BRI)
    const handleSetSupplierSource = (supplierName, bankSource) => {
        if (isDayLocked) return;
        setRekapData(prev => ({
            ...prev,
            supplierSources: {
                ...(prev.supplierSources || {}),
                [supplierName]: bankSource
            }
        }));
    };

    const handleConfirmTopup = (supplierName) => {
        setRekapData(prev => ({
            ...prev,
            confirmedTopups: {
                ...(prev.confirmedTopups || {}),
                [supplierName]: true
            }
        }));
    };

    const handleConfirmLock = async () => {
        setSyncStatus('Locking...');
        const currentSupplierSnapshot = {
            Digiflazz: displaySupplierBalances.Digiflazz,
            DAS: displaySupplierBalances.DAS,
            HAO: displaySupplierBalances.HAO,
            AMC: displaySupplierBalances.AMC,
            NEWBIEZ: displaySupplierBalances.NEWBIEZ,
            OASIS: displaySupplierBalances.OASIS,
            ANTUM: displaySupplierBalances.ANTUM
        };

        try {
            const updatePayload = { 
                ...rekapData,
                isLocked: true,
                lockedSupplierBalances: currentSupplierSnapshot,
                dailyStatsSnapshot: activeStats,
                webhookId: ID_SISTEM,
                labaBersihCalculated: results.labaBersih,
                profitCloudSistem: results.profitBasis,
                lastUpdated: new Date().toISOString()
            };
            await saveRekapData(effectiveDate, updatePayload);
            setProfitIsLocked(true);
            setIsDayLocked(true);
            setShowLockConfirm(false);
            setSyncStatus('Snapshot Saved');
            setTimeout(() => setSyncStatus('Live'), 2000);
        } catch (error) {
            setSyncStatus('Lock Failed');
        }
    };

    const handleCloudSync = async () => {
        setSyncStatus('Saving...');
        prevDocCache.current.clear();
        const finalData = {
            ...rekapData,
            webhookId: ID_SISTEM,
            labaBersihCalculated: results.labaBersih,
            isLocked: isDayLocked,
            lastUpdated: new Date().toISOString()
        };
        await saveRekapData(effectiveDate, finalData, (msg) => {
            setSyncStatus(msg);
            setTimeout(() => setSyncStatus('Live'), 2000);
        });
    };

    const addOpsItem = () => {
        if (tempOpsValue <= 0 || !tempOpsName) return;
        const newItem = { id: Date.now(), name: tempOpsName, amount: tempOpsValue };
        setRekapData(prev => ({ ...prev, biayaOpsList: [...(prev.biayaOpsList || []), newItem] }));
        setTempOpsName(''); setTempOpsValue(0);
    };

    const removeOpsItem = (id) => {
        if (isDayLocked || isOpsLock) return;
        setRekapData(prev => ({ ...prev, biayaOpsList: prev.biayaOpsList.filter(item => item.id !== id) }));
    };

    const sendToTelegram = async () => {
        if (isSendingTelegram) return;

        setIsSendingTelegram(true);
        setSendStage('excel');
        setErrorMessage('');
        setSyncStatus('Memproses Laporan Excel...');

        try {
            const f = (v) => formatIDR(v || 0);
            const wsData = [
                ["LAPORAN OPERASIONAL HARIAN"],
                ["Tanggal Laporan", ": " + effectiveDate],
                [""],
                ["I. STATISTIK TRANSAKSI", "JUMLAH"],
                ["Total Transaksi", activeStats.totalTrx || 0],
                ["Transaksi Sukses", activeStats.successTrx || 0],
                ["Transaksi Gagal", activeStats.failedTrx || 0],
                ["Success Rate", (activeStats.successRate || 0) + "%"],
                [""],
                ["II. PERFORMA PENJUALAN", "NOMINAL"],
                ["Total Penjualan (Jual)", f(activeStats.totalSales)],
                ["Total Modal (Beli)", f(activeStats.totalCost)],
                ["Laba Hari Ini (Gross)", f(results.profitKotor)],
                [""],
                ["III. RINCIAN ASET RIIL", "NOMINAL"],
                ["Bank Real (BCA+BRI - Agen)", f(results.totalBankReal)],
                ["Settled Net (Hari Ini)", f(results.settledNetValue)],
                ["Tujuan Settled Bank", rekapData.settledTargetBank || "BCA"],
                ["Saldo Midtrans", f(results.midtransValue)],
                ["Saldo Digiflazz", f(rekapData.sisaDigiflazz)],
                ["Supplier DAS", f(displaySupplierBalances.DAS.real)],
                ["Supplier AMC", f(displaySupplierBalances.AMC.real)],
                ["Supplier HAO", f(displaySupplierBalances.HAO.real)],
                ["Supplier NEWBIEZ", f(displaySupplierBalances.NEWBIEZ.real)],
                ["Supplier OASIS", f(displaySupplierBalances.OASIS.real)],
                ["Supplier ANTUM", f(displaySupplierBalances.ANTUM.real)],
                ["TOTAL ASET RIIL (FINAL)", f(results.totalSisaSaldo)],
                [""],
                ["IV. INFO PENDUKUNG (NON-ASET)", "NOMINAL"],
                ["Bank BCA (Input)", f(rekapData.saldoBCA)],
                ["Bank BRI (Input)", f(rekapData.saldoBRI)],
                ["Total Saldo Agen", f(results.totalSaldoAgen)],
                ["Digiswitch (Catatan)", f(rekapData.saldoDigiswitch)],
                ["Investasi (Catatan)", f(rekapData.investasi)],
                [""],
                ["V. RINGKASAN PERHITUNGAN", "NOMINAL"],
                ["Modal Utama", f(rekapData.modalUtama)],
                ["Laba Bersih (Aset - Modal Utama)", f(results.labaBersih)],
                ["Total Profit (System)", f(results.profitBasis)], 
                ["Akumulasi Potongan Harian", f(results.totalPotonganAccumulated)],
                ["SELISIH / VARIANCE", f(results.selisihProfit)],
                [""],
                ["VI. REKAPAN REKONSILIASI BANK"],
                ["BCA - Saldo Kemarin", f(assetBCA.saldoKemarin)],
                ["BCA - Total Penambahan", f(assetBCA.totalPenambahan)],
                ["BCA - Saldo Sekarang", f(assetBCA.saldoAktual)],
                ["BCA - Total Pengurangan", f(assetBCA.totalPengurangan)],
                ["BCA - Status", assetBCA.isNormal ? "NORMAL" : `JANGGAL (Selisih: ${f(assetBCA.selisih)})`],
                ["BRI - Saldo Kemarin", f(assetBRI.saldoKemarin)],
                ["BRI - Total Penambahan", f(assetBRI.totalPenambahan)],
                ["BRI - Saldo Sekarang", f(assetBRI.saldoAktual)],
                ["BRI - Total Pengurangan", f(assetBRI.totalPengurangan)],
                ["BRI - Status", assetBRI.isNormal ? "NORMAL" : `JANGGAL (Selisih: ${f(assetBRI.selisih)})`],
                [""],
                ["VII. PENGELUARAN OPERASIONAL", "NOMINAL"],
                ...(rekapData.biayaOpsList || []).map(item => [item.name, f(item.amount)]),
                ["TOTAL BIAYA OPS", f((rekapData.biayaOpsList || []).reduce((acc, curr) => acc + curr.amount, 0))],
                [""],
                ["VIII. CATATAN ANALISIS"],
                [rekapData.catatanAnalisisLainnya || "Tidak ada catatan."]
            ];

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(wsData);
            ws['!cols'] = [{ wch: 35 }, { wch: 25 }];
            XLSX.utils.book_append_sheet(wb, ws, "Rekapan");
            
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            const formData = new FormData();
            formData.append('chat_id', TELEGRAM_CHAT_ID);
            formData.append('document', blob, `Rekap_${effectiveDate}.xlsx`);
            
            const caption = `LAPORAN REKAPAN HARIAN\n` +
                            `Tanggal: ${effectiveDate}\n\n` +
                            `STATISTIK TRANSAKSI\n` +
                            `Sukses: ${activeStats.successTrx || 0}\n` +
                            `Gagal: ${activeStats.failedTrx || 0}\n\n` +
                            `PERFORMA KEUANGAN\n` +
                            `Laba Harian: ${f(results.profitKotor)}\n` +
                            `Laba Bersih: ${f(results.labaBersih)}\n` +
                            `Total Profit: ${f(results.profitBasis)}\n` + 
                            `Selisih: ${f(results.selisihProfit)}\n\n` +
                            `ASET FINAL\n` +
                            `Total Aset Riil: ${f(results.totalSisaSaldo)}\n` +
                            `Bank Real (Nett): ${f(results.totalBankReal)}\n` +
                            `Settled Target: ${rekapData.settledTargetBank || 'BCA'}`;

            formData.append('caption', caption);
            formData.append('parse_mode', 'HTML');

            const response = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendDocument`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                setSendStage('supplier');
                setSyncStatus('Mengirim Rincian Saldo Supplier...');
                
                // Susun daftar supplier secara dinamis & perataan spasi otomatis
                const supplierListRaw = [
                    { name: 'DAS', val: f(displaySupplierBalances.DAS.real) },
                    { name: 'AMC', val: f(displaySupplierBalances.AMC.real) },
                    { name: 'HAO', val: f(displaySupplierBalances.HAO.real) },
                    { name: 'NEWBIEZ', val: f(displaySupplierBalances.NEWBIEZ.real) },
                    { name: 'OASIS', val: f(displaySupplierBalances.OASIS.real) },
                    { name: 'ANTUM', val: f(displaySupplierBalances.ANTUM.real) }
                ];

                const maxLen = Math.max(...supplierListRaw.map(item => item.name.length));
                const formattedSupplierLines = supplierListRaw.map(item => {
                    const paddedName = item.name.padEnd(maxLen + 10, ' ');
                    return `${paddedName}: ${item.val}`;
                }).join('\n');

                const messageSupplier = `LAPORAN SISA SALDO SUPPLIER\n` +
                                       `Tanggal: ${effectiveDate}\n\n` +
                                       `${formattedSupplierLines}\n\n` +
                                       `TOTAL SALDO SUPPLIER: ${f(results.totalSupplierBalance)}`;

                const respSupplier = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: TELEGRAM_CHAT_ID,
                        text: messageSupplier,
                        parse_mode: 'HTML'
                    })
                });

                if (respSupplier.ok) {
                    setSendStage('success');
                    setSyncStatus('Terkirim Ke Telegram!');
                    setTimeout(() => {
                        setSendStage('idle');
                        setIsSendingTelegram(false);
                        setIsAssetModalOpen(false);
                        setSyncStatus('Live');
                    }, 1200);
                } else {
                    throw new Error('Laporan Excel terkirim, tetapi gagal mengirim laporan saldo supplier.');
                }
            } else {
                const errData = await response.json();
                throw new Error(errData.description || 'Gagal mengirim laporan rekapan harian');
            }
        } catch (error) {
            setSendStage('error');
            setErrorMessage(error.message || 'Terjadi kesalahan sistem saat mengirim laporan.');
            setSyncStatus('Gagal Terkirim');
        }
    };

    return (
        <ErrorBoundary>
            <style>{LOADING_CSS}</style>
            <TopProgressBar active={(isInitialLoading && !loadError) || agentsLoading} />
            <div className="bg-slate-50 p-3 md:p-8 font-sans text-slate-900 rounded-[2rem] min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <header className="mb-6 md:mb-10 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-800 uppercase">Operational Dashboard</h1>
                            <p className="text-slate-500 font-medium text-[10px] md:text-xs uppercase tracking-widest flex items-center gap-2">
                                Laporan Harian Terminal {syncStatus && (
                                    isInitialLoading && !loadError
                                        ? <span className="text-blue-600 inline-flex items-center gap-1">| Memuat<BouncingDots /></span>
                                        : <span className={loadError ? 'text-red-600' : 'text-blue-600 animate-pulse'}>| {syncStatus}</span>
                                )}
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch gap-3">
                            <button onClick={handleResetSelectedDate} disabled={isDayLocked} className="px-5 py-3 md:py-4 rounded-2xl font-black text-xs md:text-sm flex items-center justify-center gap-2 bg-red-50 text-red-600 border border-red-100 hover:bg-red-600 hover:text-white transition-all active:scale-95 disabled:opacity-50">
                                <RotateCcw size={16} /> RESET TANGGAL INI
                            </button>
                            <button onClick={handleCloudSync} className="px-5 py-3 md:py-4 rounded-2xl font-black text-xs md:text-sm flex items-center justify-center gap-2 bg-blue-600 text-white shadow-lg active:scale-95 transition-all">
                                <Save size={16} /> SIMPAN
                            </button>
                            <button onClick={() => { if(!isDayLocked) setShowLockConfirm(true); else setIsDayLocked(false); }} className={`px-5 py-3 md:py-4 rounded-2xl font-black text-xs md:text-sm flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95 ${isDayLocked ? 'bg-orange-500 text-white' : 'bg-white text-slate-700 border border-slate-200'}`}>
                                {isDayLocked ? <Lock size={16} /> : <Unlock size={16} />} {isDayLocked ? 'LOCKED' : 'LOCK DAY'}
                            </button>
                            <div className="bg-white px-4 py-3 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between gap-3">
                                <Calendar size={16} className="text-blue-600" />
                                <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="font-bold text-xs outline-none text-slate-700 cursor-pointer bg-transparent" />
                            </div>
                        </div>
                    </header>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <div className="bg-white p-5 rounded-[2rem] border border-slate-200 shadow-sm">
                            <p className="text-[8px] font-black text-slate-400 uppercase mb-1">Total Transaksi</p>
                            <h4 className="text-lg font-black text-slate-800">{activeStats.totalTrx} <span className="text-[10px] text-slate-400">Trx</span></h4>
                        </div>
                        <div className="bg-white p-5 rounded-[2rem] border border-slate-200 shadow-sm">
                            <p className="text-[8px] font-black text-emerald-500 uppercase mb-1">Success Rate</p>
                            <h4 className="text-lg font-black text-emerald-600">{activeStats.successRate}%</h4>
                        </div>
                        <div className="bg-white p-5 rounded-[2rem] border border-slate-200 shadow-sm">
                            <p className="text-[8px] font-black text-blue-500 uppercase mb-1">Penjualan Hari Ini</p>
                            <h4 className="text-lg font-black text-blue-600">{formatIDR(activeStats.totalSales)}</h4>
                        </div>
                        <div className="bg-white p-5 rounded-[2rem] border border-slate-200 shadow-sm">
                            <p className="text-[8px] font-black text-orange-500 uppercase mb-1">Profit Kotor</p>
                            <h4 className="text-lg font-black text-orange-600">{formatIDR(results.profitKotor)}</h4>
                        </div>
                    </div>

                    {(isInitialLoading || loadError) ? (
                        <LoadingPanel error={loadError} onRetry={handleRetryLoad} stage={agentsLoading ? 'Menghubungkan ke server' : 'Mengambil data rekapan'} />
                    ) : (<>
                    <div className="w-full mb-8">
                        <div className="bg-white p-5 md:p-8 rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden relative">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xs font-black uppercase text-blue-600 flex items-center gap-2 tracking-widest"><Wallet size={16} /> 1. Komponen Aset & Info</h3>
                                <button onClick={() => setIsSupplierEdit(!isSupplierEdit)} className="text-[10px] font-black underline text-blue-700 uppercase flex items-center gap-1">
                                    <Edit3 size={12}/> {isSupplierEdit ? 'Selesai' : 'Edit Supplier'}
                                </button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                                <StableInput id="saldoBCA" value={rekapData.saldoBCA} onChange={handleManualChange} label="BCA (Info)" icon={<CreditCard size={10}/>} color="text-slate-400" bg="bg-slate-50" border="focus:border-blue-200" disabled={isDayLocked} />
                                <StableInput id="saldoBRI" value={rekapData.saldoBRI} onChange={handleManualChange} label="BRI (Info)" icon={<Landmark size={10}/>} color="text-slate-400" bg="bg-slate-50" border="focus:border-orange-200" disabled={isDayLocked} />
                                
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center ml-2">
                                        <label className="text-[10px] font-black text-emerald-600 uppercase flex items-center gap-1">
                                            <Calculator size={10}/> Settled Digiflazz
                                        </label>
                                        <div className="flex items-center gap-1 bg-emerald-100/60 p-0.5 rounded-lg border border-emerald-200">
                                            <button 
                                                type="button"
                                                onClick={() => handleManualChange('settledTargetBank', 'BCA')}
                                                disabled={isDayLocked}
                                                className={`text-[9px] font-black px-2 py-0.5 rounded-md transition-all ${
                                                    (rekapData.settledTargetBank || 'BCA') === 'BCA' 
                                                        ? 'bg-blue-600 text-white shadow-sm' 
                                                        : 'text-slate-500 hover:text-slate-800'
                                                }`}
                                            >
                                                BCA
                                            </button>
                                            <button 
                                                type="button"
                                                onClick={() => handleManualChange('settledTargetBank', 'BRI')}
                                                disabled={isDayLocked}
                                                className={`text-[9px] font-black px-2 py-0.5 rounded-md transition-all ${
                                                    rekapData.settledTargetBank === 'BRI' 
                                                        ? 'bg-orange-500 text-white shadow-sm' 
                                                        : 'text-slate-500 hover:text-slate-800'
                                                }`}
                                            >
                                                BRI
                                            </button>
                                        </div>
                                    </div>
                                    <StableInput id="settledDigiflazz" value={rekapData.settledDigiflazz} onChange={handleManualChange} color="text-emerald-600" bg="bg-emerald-50/30" border="focus:border-emerald-500" disabled={isDayLocked} />
                                </div>

                                <StableInput id="saldoMidtrans" value={rekapData.saldoMidtrans} onChange={handleManualChange} label="Saldo Midtrans" icon={<Wallet size={10}/>} color="text-blue-600" bg="bg-blue-50/30" border="focus:border-blue-500" disabled={isDayLocked} />
                                <StableInput id="saldoDigiswitch" value={rekapData.saldoDigiswitch} onChange={handleManualChange} label="Digiswitch (Catatan)" icon={<Zap size={10}/>} color="text-slate-400" bg="bg-slate-50" border="focus:border-slate-300" disabled={isDayLocked} />
                            </div>

                            <div className="w-full px-6 py-4 bg-blue-50/50 rounded-2xl flex flex-col md:flex-row justify-between items-center gap-4 border border-blue-100 mb-6">
                                <div className="flex items-center gap-4">
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">Total Saldo Agen:</span>
                                        <span className="text-sm font-black text-red-500">{formatIDR(results.totalSaldoAgen)}</span>
                                    </div>
                                    <div className="h-8 w-[1px] bg-blue-200 hidden md:block"></div>
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-black text-blue-600 uppercase tracking-tighter">Bank Real (Nett Asset):</span>
                                        <span className="text-sm font-black text-blue-700">{formatIDR(results.totalBankReal)}</span>
                                    </div>
                                </div>
                                <div className="bg-white/50 px-4 py-2 rounded-xl border border-blue-200 hidden md:block">
                                    <span className="text-[10px] font-black text-blue-500 uppercase">Status: Terkonsolidasi</span>
                                </div>
                            </div>

                            {/* Tampilan Rinci Rekonsiliasi Status Saldo BCA & BRI */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div className="bg-slate-50 p-6 md:p-8 rounded-[2rem] border border-slate-200">
                                    <div className="flex flex-col justify-between items-start mb-4 gap-4">
                                        <div className="space-y-3 w-full">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-pulse"></div>
                                                    <span className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em]">Total Asset BCA</span>
                                                </div>
                                                <h2 className="text-3xl md:text-4xl font-black text-slate-800 tracking-tighter">{formatIDR(assetBCA.saldoAktual)}</h2>
                                            </div>
                                            
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3 border-t border-slate-200">
                                                <div className="flex flex-col bg-white p-2.5 rounded-xl border border-slate-100">
                                                    <span className="text-[8px] font-bold text-slate-400 uppercase">Saldo Kemarin</span>
                                                    <span className="text-[11px] font-black text-slate-700">{formatIDR(assetBCA.saldoKemarin)}</span>
                                                </div>
                                                <div className="flex flex-col bg-white p-2.5 rounded-xl border border-slate-100">
                                                    <span className="text-[8px] font-bold text-emerald-500 uppercase">Penambahan (+)</span>
                                                    <span className="text-[11px] font-black text-emerald-600">+{formatIDR(assetBCA.totalPenambahan)}</span>
                                                </div>
                                                <div className="flex flex-col bg-white p-2.5 rounded-xl border border-slate-100">
                                                    <span className="text-[8px] font-bold text-slate-400 uppercase">Saldo Sekarang</span>
                                                    <span className="text-[11px] font-black text-slate-700">{formatIDR(assetBCA.saldoAktual)}</span>
                                                </div>
                                                <div className="flex flex-col bg-white p-2.5 rounded-xl border border-slate-100">
                                                    <span className="text-[8px] font-bold text-red-500 uppercase">Pengurangan (-)</span>
                                                    <span className="text-[11px] font-black text-red-600">-{formatIDR(assetBCA.totalPengurangan)}</span>
                                                </div>
                                            </div>

                                            <div className="p-3 bg-white rounded-xl border border-slate-200 flex items-center justify-between mt-2">
                                                <div className="flex flex-col">
                                                    <span className="text-[8px] font-black text-slate-400 uppercase">Penambahan Supplier via BCA: <span className="text-blue-600">{formatIDR(assetBCA.penambahanSupplier)}</span></span>
                                                    <span className="text-[8px] font-black text-slate-400 uppercase">Pengurangan BCA: <span className="text-red-500">{formatIDR(assetBCA.totalPengurangan)}</span></span>
                                                </div>
                                                <div className="text-right">
                                                    {assetBCA.isNormal ? (
                                                        <span className="text-xs font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-200 flex items-center gap-1"><CheckCircle2 size={12}/> NORMAL</span>
                                                    ) : (
                                                        <div className="flex flex-col items-end">
                                                            <span className="text-xs font-black text-red-600 bg-red-50 px-3 py-1 rounded-lg border border-red-200 flex items-center gap-1"><AlertTriangle size={12}/> JANGGAL</span>
                                                            <span className="text-[9px] font-black text-red-600 mt-1">Selisih: {formatIDR(assetBCA.selisih)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-slate-50 p-6 md:p-8 rounded-[2rem] border border-slate-200">
                                    <div className="flex flex-col justify-between items-start mb-4 gap-4">
                                        <div className="space-y-3 w-full">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <div className="w-2.5 h-2.5 rounded-full bg-orange-500 animate-pulse"></div>
                                                    <span className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em]">Total Asset BRI</span>
                                                </div>
                                                <h2 className="text-3xl md:text-4xl font-black text-slate-800 tracking-tighter">{formatIDR(assetBRI.saldoAktual)}</h2>
                                            </div>
                                            
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3 border-t border-slate-200">
                                                <div className="flex flex-col bg-white p-2.5 rounded-xl border border-slate-100">
                                                    <span className="text-[8px] font-bold text-slate-400 uppercase">Saldo Kemarin</span>
                                                    <span className="text-[11px] font-black text-slate-700">{formatIDR(assetBRI.saldoKemarin)}</span>
                                                </div>
                                                <div className="flex flex-col bg-white p-2.5 rounded-xl border border-slate-100">
                                                    <span className="text-[8px] font-bold text-emerald-500 uppercase">Penambahan (+)</span>
                                                    <span className="text-[11px] font-black text-emerald-600">+{formatIDR(assetBRI.totalPenambahan)}</span>
                                                </div>
                                                <div className="flex flex-col bg-white p-2.5 rounded-xl border border-slate-100">
                                                    <span className="text-[8px] font-bold text-slate-400 uppercase">Saldo Sekarang</span>
                                                    <span className="text-[11px] font-black text-slate-700">{formatIDR(assetBRI.saldoAktual)}</span>
                                                </div>
                                                <div className="flex flex-col bg-white p-2.5 rounded-xl border border-slate-100">
                                                    <span className="text-[8px] font-bold text-red-500 uppercase">Pengurangan (-)</span>
                                                    <span className="text-[11px] font-black text-red-600">-{formatIDR(assetBRI.totalPengurangan)}</span>
                                                </div>
                                            </div>

                                            <div className="p-3 bg-white rounded-xl border border-slate-200 flex items-center justify-between mt-2">
                                                <div className="flex flex-col">
                                                    <span className="text-[8px] font-black text-slate-400 uppercase">Penambahan Supplier via BRI: <span className="text-orange-600">{formatIDR(assetBRI.penambahanSupplier)}</span></span>
                                                    <span className="text-[8px] font-black text-slate-400 uppercase">Pengurangan BRI: <span className="text-red-500">{formatIDR(assetBRI.totalPengurangan)}</span></span>
                                                </div>
                                                <div className="text-right">
                                                    {assetBRI.isNormal ? (
                                                        <span className="text-xs font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-200 flex items-center gap-1"><CheckCircle2 size={12}/> NORMAL</span>
                                                    ) : (
                                                        <div className="flex flex-col items-end">
                                                            <span className="text-xs font-black text-red-600 bg-red-50 px-3 py-1 rounded-lg border border-red-200 flex items-center gap-1"><AlertTriangle size={12}/> JANGGAL</span>
                                                            <span className="text-[9px] font-black text-red-600 mt-1">Selisih: {formatIDR(assetBRI.selisih)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section Total Aset Supplier */}
                            <div className="bg-slate-50 p-6 md:p-8 rounded-[2rem] border border-slate-200">
                                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                                    <div className="space-y-3">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <div className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-pulse"></div>
                                                <span className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">Total Asset Supplier</span>
                                            </div>
                                            <h2 className="text-3xl md:text-4xl font-black text-slate-800 tracking-tighter">{formatIDR(results.totalSupplierBalance)}</h2>
                                        </div>
                                        
                                        <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2 border-t border-slate-200">
                                            <div className="flex flex-col">
                                                <span className="text-[8px] font-bold text-slate-400 uppercase">Saldo Kemarin</span>
                                                <span className="text-[11px] font-black text-slate-600">{formatIDR(displaySupplierBalances.aggregate.kemarin)}</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[8px] font-bold text-red-400 uppercase">Total Pengurangan</span>
                                                <span className="text-[11px] font-black text-red-500">-{formatIDR(displaySupplierBalances.aggregate.pakai)}</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[8px] font-bold text-emerald-400 uppercase">Total Penambahan</span>
                                                <span className="text-[11px] font-black text-emerald-600">+{formatIDR(displaySupplierBalances.aggregate.tambah)}</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-[8px] font-bold text-slate-400 uppercase">Status Saldo</span>
                                                {displaySupplierBalances.aggregate.isAnyJanggal ? (
                                                    <span className="text-[11px] font-black text-orange-600 flex items-center gap-1"><AlertTriangle size={10}/> JANGGAL</span>
                                                ) : (
                                                    <span className="text-[11px] font-black text-emerald-600 flex items-center gap-1"><CheckCircle2 size={10}/> NORMAL</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white border border-slate-200 px-5 py-4 rounded-2xl flex items-center gap-4 w-full md:max-w-md shadow-sm">
                                        <div className="p-2 bg-blue-50 rounded-xl flex-shrink-0"><Calculator size={20} className="text-blue-600"/></div>
                                        <div className="flex flex-col w-full">
                                            <span className="text-[9px] font-black text-slate-400 uppercase mb-1 tracking-wider">Sisa Saldo Digiflazz</span>
                                            <input 
                                                type="text" 
                                                value={formatIDR(rekapData.sisaDigiflazz)} 
                                                onChange={(e) => handleManualChange('sisaDigiflazz', parseRawNumber(e.target.value))}
                                                className="bg-transparent text-slate-800 font-black text-lg outline-none border-none w-full focus:text-blue-600 transition-colors"
                                                disabled={isDayLocked}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* List Supplier dengan Pilihan Sumber Penambahan Dana (BCA / BRI) */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {['DAS', 'AMC', 'HAO', 'NEWBIEZ', 'OASIS', 'ANTUM'].map((name) => {
                                        const data = displaySupplierBalances[name];
                                        const isJanggal = data.isJanggal;
                                        const hasPenambahan = data.tambah > 0;

                                        return (
                                            <div key={name} className={`flex flex-col p-5 rounded-[1.5rem] border transition-all ${isJanggal ? 'bg-orange-50 border-orange-200 shadow-md' : 'bg-white border-slate-100 hover:shadow-sm'}`}>
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-[10px] ${isJanggal ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                                            {name.substring(0, 3).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <h4 className="font-black text-slate-800 text-xs uppercase flex items-center gap-2">
                                                                {name} 
                                                                {isJanggal ? (
                                                                    <span className="bg-orange-500 text-white text-[7px] px-2 py-0.5 rounded animate-pulse">JANGGAL</span>
                                                                ) : (
                                                                    <span className="text-emerald-500 text-[8px] font-black flex items-center gap-1">● NORMAL</span>
                                                                )}
                                                            </h4>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                {isSupplierEdit ? (
                                                                    <input 
                                                                        type="text" 
                                                                        className="bg-blue-50 text-blue-600 text-xs font-black px-2 py-1 rounded border border-blue-200 w-32 outline-none" 
                                                                        value={formatIDR(data.real)} 
                                                                        onChange={(e) => handleSupplierManualEdit(name, parseRawNumber(e.target.value))} 
                                                                    />
                                                                ) : (
                                                                    <span className={`text-sm font-black ${isJanggal ? 'text-orange-600' : 'text-slate-700'}`}>{formatIDR(data.real)}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {isJanggal && (
                                                        <button onClick={() => handleConfirmTopup(name)} className="bg-orange-500 hover:bg-orange-600 text-white text-[8px] font-black px-3 py-2 rounded-lg uppercase transition-all">Konfirmasi</button>
                                                    )}
                                                </div>

                                                {/* Pilihan Sumber Penambahan Melalui BCA / BRI (Internal System Tetap Ada) */}
                                                {hasPenambahan && (
                                                    <div className="p-2.5 mb-3 bg-blue-50/60 rounded-xl border border-blue-100 flex items-center justify-between">
                                                        <span className="text-[9px] font-black text-blue-700 uppercase">Penambahan Melalui:</span>
                                                        <div className="flex items-center gap-1.5">
                                                            <button 
                                                                type="button"
                                                                onClick={() => handleSetSupplierSource(name, 'BCA')}
                                                                disabled={isDayLocked}
                                                                className={`text-[9px] font-black px-3 py-1 rounded-lg transition-all ${
                                                                    data.sourceBank === 'BCA' 
                                                                        ? 'bg-blue-600 text-white shadow-sm' 
                                                                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-55'
                                                                }`}
                                                            >
                                                                BCA
                                                            </button>
                                                            <button 
                                                                type="button"
                                                                onClick={() => handleSetSupplierSource(name, 'BRI')}
                                                                disabled={isDayLocked}
                                                                className={`text-[9px] font-black px-3 py-1 rounded-lg transition-all ${
                                                                    data.sourceBank === 'BRI' 
                                                                        ? 'bg-orange-500 text-white shadow-sm' 
                                                                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-55'
                                                                }`}
                                                            >
                                                                BRI
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="grid grid-cols-4 gap-2 border-t border-slate-100 pt-3 text-center">
                                                    <div className="flex flex-col">
                                                        <span className="text-[7px] font-black text-slate-400 uppercase">Kemarin</span>
                                                        <span className="text-[10px] font-bold text-slate-600">{formatIDR(data.kemarin)}</span>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[7px] font-black text-red-400 uppercase">Pakai (-)</span>
                                                        <span className="text-[10px] font-bold text-red-500">-{formatIDR(data.pakai)}</span>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[7px] font-black text-slate-400 uppercase">Sekarang</span>
                                                        <span className="text-[10px] font-bold text-slate-700">{formatIDR(data.real)}</span>
                                                    </div>
                                                    <div className="flex flex-col items-end">
                                                        <span className="text-[7px] font-black text-emerald-500 uppercase">Tambah (+)</span>
                                                        <span className={`text-[10px] font-bold ${data.tambah > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>+{formatIDR(data.tambah)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-8 p-6 md:p-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[3rem] text-white shadow-2xl relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform"><Wallet size={120} /></div>
                        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left">
                            <div>
                                <p className="text-blue-100 font-black text-[10px] md:text-xs uppercase tracking-[0.2em] mb-2">Laba Bersih (Aset Final - Modal Utama)</p>
                                <h2 className="text-4xl md:text-6xl font-black tracking-tighter">{formatIDR(results.labaBersih)}</h2>
                            </div>
                            <div onClick={() => setIsAssetModalOpen(true)} className="bg-white/20 backdrop-blur-md p-6 rounded-[2.5rem] border border-white/30 text-center min-w-[240px] cursor-pointer hover:bg-white/40 transition-all active:scale-95 shadow-xl">
                                <p className="text-[10px] font-black uppercase mb-1 flex items-center justify-center gap-2 tracking-[0.1em]"><Info size={12}/> Detail Aset Riil <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform"/></p>
                                <p className="text-2xl md:text-3xl font-black">{formatIDR(results.totalSisaSaldo)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                        <div className="bg-slate-900 p-6 md:p-8 rounded-[2.5rem] text-white flex justify-between items-center shadow-xl">
                            <div>
                                <div className="flex items-center gap-3 mb-1">
                                    <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Total Profit (Auto)</p> 
                                    {profitIsLocked && <span className="bg-blue-500/20 text-blue-400 text-[8px] px-2 py-0.5 rounded-full font-black border border-blue-500/30">LOCKED</span>}
                                </div>
                                <h2 className="text-3xl font-black">{formatIDR(results.profitBasis)}</h2>
                                <p className="text-[10px] text-emerald-400 font-black uppercase mt-2">Laba Hari Ini: {formatIDR(results.profitKotor)}</p>
                            </div>
                            <TrendingUp size={32} className={`${profitIsLocked ? 'text-blue-400' : 'text-emerald-400 animate-pulse'}`} />
                        </div>

                        <div onClick={() => setIsDiffModalOpen(true)} className="bg-white p-6 md:p-8 rounded-[2.5rem] border-2 border-indigo-100 shadow-sm flex justify-between items-center cursor-pointer hover:border-indigo-300 transition-all">
                            <div>
                                <div className="flex items-center gap-2 text-indigo-600 mb-1 font-black text-[10px] uppercase tracking-wider">
                                    <Scale size={14} /> Selisih (Laba - Profit)
                                </div>
                                <h2 className={`text-3xl font-black ${results.selisihProfit < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatIDR(results.selisihProfit)}</h2>
                                <p className="text-[8px] mt-2 text-slate-400 font-bold uppercase tracking-tight">Akumulasi Potongan Harian: {formatIDR(results.totalPotonganAccumulated)}</p>
                            </div>
                            <Scale size={32} className="opacity-10 text-indigo-600" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* BAGIAN 2. BIAYA OPERASIONAL */}
                        <div className="bg-white p-5 md:p-8 rounded-[2.5rem] shadow-sm border border-slate-100 lg:col-span-1 flex flex-col justify-between">
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-xs font-black uppercase text-amber-600 flex items-center gap-2 tracking-widest">
                                        <Coffee size={18} className="shrink-0 text-amber-600" /> 2. Biaya Operasional
                                    </h3>
                                    <button 
                                        onClick={() => setIsOpsLock(!isOpsLock)} 
                                        disabled={isDayLocked} 
                                        className="text-[10px] font-black underline text-amber-700 hover:text-amber-800 uppercase transition-colors shrink-0"
                                    >
                                        {isOpsLock ? 'BUKA' : 'KUNCI'}
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                                        <input 
                                            type="text" 
                                            placeholder="Keterangan..." 
                                            value={tempOpsName} 
                                            onChange={(e) => setTempOpsName(e.target.value)} 
                                            disabled={isOpsLock || isDayLocked} 
                                            className="sm:col-span-6 p-3.5 bg-amber-50/30 rounded-2xl outline-none font-bold text-xs border border-transparent focus:border-amber-200 transition-all" 
                                        />
                                        <input 
                                            type="text" 
                                            placeholder="Rp 0" 
                                            value={tempOpsValue === 0 ? '' : formatIDR(tempOpsValue)} 
                                            onChange={(e) => setTempOpsValue(parseRawNumber(e.target.value))} 
                                            disabled={isOpsLock || isDayLocked} 
                                            className="sm:col-span-4 p-3.5 bg-amber-50/30 rounded-2xl outline-none font-bold text-xs text-right border border-transparent focus:border-amber-200 transition-all" 
                                        />
                                        <button 
                                            onClick={addOpsItem} 
                                            disabled={isOpsLock || isDayLocked} 
                                            className="sm:col-span-2 p-3.5 bg-amber-500 text-white rounded-2xl hover:bg-amber-600 transition-all active:scale-95 flex items-center justify-center disabled:opacity-50 shadow-md shadow-amber-500/20"
                                            title="Tambah Biaya"
                                        >
                                            <Plus size={18} />
                                        </button>
                                    </div>

                                    <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1 custom-scrollbar">
                                        {rekapData.biayaOpsList?.length > 0 ? (
                                            rekapData.biayaOpsList.map((item) => (
                                                <div key={item.id} className="flex items-center justify-between p-3.5 bg-slate-50 rounded-2xl border border-slate-100 group hover:border-amber-200 transition-all gap-3">
                                                    <span className="text-xs font-bold text-slate-700 truncate min-w-0 flex-1">{item.name}</span>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <span className="text-xs font-black text-amber-600">{formatIDR(item.amount)}</span>
                                                        {(!isOpsLock && !isDayLocked) && (
                                                            <button 
                                                                onClick={() => removeOpsItem(item.id)} 
                                                                className="text-slate-400 hover:text-red-500 p-1 rounded-lg transition-colors"
                                                                title="Hapus Biaya"
                                                            >
                                                                <Trash2 size={15} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center py-6 text-[11px] font-bold text-slate-400 border border-dashed border-slate-200 rounded-2xl">
                                                Belum ada rincian biaya operasional
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-red-50/40 p-6 md:p-8 rounded-[2.5rem] border-2 border-red-100/50 flex flex-col justify-center h-full min-h-[200px] lg:col-span-1">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-[10px] font-black uppercase text-red-600 flex items-center gap-2 tracking-widest"><PiggyBank size={16} /> Modal Utama</h3>
                                <button onClick={() => setIsModalLock(!isModalLock)} disabled={isDayLocked} className="text-[10px] font-black underline text-red-700 uppercase">{isModalLock ? 'BUKA' : 'KUNCI'}</button>
                            </div>
                            <input 
                                type="text"
                                inputMode="numeric"
                                value={formatIDR(rekapData.modalUtama)}
                                onChange={(e) => handleManualChange('modalUtama', parseRawNumber(e.target.value))}
                                disabled={isModalLock || isDayLocked}
                                className="w-full px-6 py-8 bg-white/80 rounded-[2rem] border-2 border-transparent focus:border-red-200 outline-none text-3xl font-black text-red-600 transition-all disabled:opacity-60"
                            />
                        </div>

                        <div className="bg-purple-50/40 p-6 md:p-8 rounded-[2.5rem] border-2 border-purple-100/50 flex flex-col justify-center h-full min-h-[200px] lg:col-span-1">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-[10px] font-black uppercase text-purple-600 flex items-center gap-2 tracking-widest"><Landmark size={16} /> Investasi (Catatan)</h3>
                                <button onClick={() => setIsInvestasiLock(!isInvestasiLock)} disabled={isDayLocked} className="text-[10px] font-black underline text-purple-700 uppercase">{isInvestasiLock ? 'BUKA' : 'KUNCI'}</button>
                            </div>
                            <input 
                                type="text"
                                inputMode="numeric"
                                value={formatIDR(rekapData.investasi)}
                                onChange={(e) => handleManualChange('investasi', parseRawNumber(e.target.value))}
                                disabled={isInvestasiLock || isDayLocked}
                                className="w-full px-6 py-8 bg-white/80 rounded-[2rem] border-2 border-transparent focus:border-purple-200 outline-none text-3xl font-black text-purple-600 transition-all disabled:opacity-60"
                            />
                        </div>
                    </div>
                    </>)}
                </div>

                {/* Modal Detail Aset Riil & Pengiriman Telegram */}
                {isAssetModalOpen && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-md" onClick={() => !isSendingTelegram && setIsAssetModalOpen(false)} />
                        <div className="relative bg-white w-full max-w-2xl rounded-[3.5rem] overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
                            <div className="bg-slate-50 p-6 md:p-8 border-b border-slate-100 flex justify-between items-center">
                                <div className="flex items-center gap-4">
                                    <div className="bg-blue-600 p-3 rounded-2xl text-white shadow-lg"><Wallet size={24} /></div>
                                    <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter">Konsolidasi Aset Final</h3>
                                </div>
                                <button onClick={() => !isSendingTelegram && setIsAssetModalOpen(false)} disabled={isSendingTelegram} className="p-3 bg-white shadow-sm rounded-full text-slate-400 hover:text-red-500 disabled:opacity-50"><X size={20}/></button>
                            </div>
                            <div className="p-8 flex flex-col gap-3">
                                <button 
                                    onClick={sendToTelegram} 
                                    disabled={isSendingTelegram}
                                    className={`relative overflow-hidden w-full py-5 rounded-[1.8rem] font-black uppercase text-xs tracking-widest flex items-center justify-center gap-3 transition-all duration-300 shadow-xl ${
                                        sendStage === 'success' 
                                            ? 'bg-emerald-500 text-white shadow-emerald-500/30 scale-[1.02]' 
                                            : sendStage === 'error'
                                            ? 'bg-red-600 text-white shadow-red-600/30'
                                            : isSendingTelegram
                                            ? 'bg-slate-800 text-white cursor-wait'
                                            : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-600/20 active:scale-[0.98]'
                                    }`}
                                >
                                    {isSendingTelegram && sendStage !== 'error' && (
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse" />
                                    )}

                                    {sendStage === 'idle' && (
                                        <>
                                            <Send size={20} className="transition-transform group-hover:translate-x-1" />
                                            <span>Kirim Laporan Ke Telegram (Excel)</span>
                                        </>
                                    )}

                                    {sendStage === 'excel' && (
                                        <>
                                            <Loader2 size={20} className="animate-spin text-emerald-400" />
                                            <span className="animate-pulse">Menyiapkan Laporan Excel...</span>
                                        </>
                                    )}

                                    {sendStage === 'supplier' && (
                                        <>
                                            <Loader2 size={20} className="animate-spin text-blue-400" />
                                            <span className="animate-pulse">Mengirim Rincian Saldo Supplier...</span>
                                        </>
                                    )}

                                    {sendStage === 'success' && (
                                        <>
                                            <div className="bg-white/20 p-1 rounded-full animate-bounce">
                                                <Check size={18} className="text-white" />
                                            </div>
                                            <span className="flex items-center gap-2">Laporan Terkirim Sukses! <Sparkles size={16}/></span>
                                        </>
                                    )}

                                    {sendStage === 'error' && (
                                        <>
                                            <AlertTriangle size={20} />
                                            <span>Pengiriman Gagal — Coba Lagi</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal Popup Loading Modern & Status Pengiriman Excel ke Telegram */}
                {isSendingTelegram && (
                    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-200">
                        <div className="bg-white p-8 md:p-10 rounded-[3rem] shadow-2xl max-w-sm w-full text-center border border-slate-100 relative overflow-hidden">
                            {sendStage !== 'error' && (
                                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-emerald-500 to-indigo-500 animate-pulse" />
                            )}

                            {sendStage === 'excel' && (
                                <div className="flex flex-col items-center py-4">
                                    <div className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-3xl flex items-center justify-center mb-6 shadow-inner relative">
                                        <Loader2 size={40} className="animate-spin text-emerald-600" />
                                    </div>
                                    <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight mb-2">Memproses Excel</h3>
                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider animate-pulse">Menyiapkan & Mengirim Laporan...</p>
                                </div>
                            )}

                            {sendStage === 'supplier' && (
                                <div className="flex flex-col items-center py-4">
                                    <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-3xl flex items-center justify-center mb-6 shadow-inner relative">
                                        <Loader2 size={40} className="animate-spin text-blue-600" />
                                    </div>
                                    <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight mb-2">Mengirim Saldo Supplier</h3>
                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider animate-pulse">Menghubungkan ke Telegram...</p>
                                </div>
                            )}

                            {sendStage === 'success' && (
                                <div className="flex flex-col items-center py-4">
                                    <div className="w-20 h-20 bg-emerald-500 text-white rounded-3xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/30 scale-110 transition-transform">
                                        <Check size={44} className="animate-bounce" />
                                    </div>
                                    <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-1">Berhasil Terkirim!</h3>
                                    <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider flex items-center gap-1 justify-center">Laporan Siap Di Telegram <Sparkles size={14}/></p>
                                </div>
                            )}

                            {sendStage === 'error' && (
                                <div className="flex flex-col items-center py-2">
                                    <div className="w-20 h-20 bg-red-50 text-red-600 rounded-3xl flex items-center justify-center mb-6 shadow-inner">
                                        <AlertTriangle size={40} className="animate-bounce" />
                                    </div>
                                    <h3 className="text-lg font-black text-red-600 uppercase tracking-tight mb-2">Pengiriman Gagal</h3>
                                    <div className="p-4 bg-red-50 rounded-2xl border border-red-100 text-left mb-6 w-full">
                                        <p className="text-[11px] font-bold text-red-700 leading-relaxed break-words">{errorMessage}</p>
                                    </div>
                                    <button 
                                        onClick={() => {
                                            setIsSendingTelegram(false);
                                            setSendStage('idle');
                                        }}
                                        className="w-full py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg active:scale-95 transition-all"
                                    >
                                        Tutup & Perbaiki
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {isDiffModalOpen && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-md" onClick={() => setIsDiffModalOpen(false)} />
                        <div className="relative bg-white w-full max-w-md rounded-[3rem] p-8 shadow-2xl animate-in slide-in-from-bottom-4 duration-200">
                            <h3 className="text-xl font-black mb-6 flex items-center gap-3 uppercase text-indigo-600 tracking-widest"><Scale size={20}/> Edit & Analisis</h3>
                            <div className="space-y-5">
                                <input type="text" value={rekapData.manualDifference === null ? '' : formatIDR(rekapData.manualDifference)} onChange={(e) => handleManualChange('manualDifference', parseRawNumber(e.target.value))} className="w-full p-5 bg-slate-50 rounded-2xl border border-slate-200 font-black text-slate-700 outline-none focus:border-indigo-400 transition-all" placeholder="Nominal koreksi..." />
                                <textarea value={rekapData.catatanAnalisisLainnya} onChange={(e) => handleManualChange('catatanAnalisisLainnya', e.target.value)} placeholder="Alasan selisih saldo..." className="w-full p-5 bg-slate-50 rounded-2xl border border-slate-200 text-xs font-bold min-h-[160px] resize-none outline-none focus:border-indigo-400 transition-all shadow-inner"/>
                            </div>
                            <button onClick={() => { handleCloudSync(); setIsDiffModalOpen(false); }} className="w-full mt-8 py-5 bg-slate-900 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-3 active:scale-95 shadow-lg"><Save size={18}/> Simpan Perubahan</button>
                        </div>
                    </div>
                )}
                
                {showLockConfirm && (
                    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
                        <div className="relative bg-white p-10 rounded-[3rem] max-w-sm w-full text-center shadow-2xl animate-in zoom-in-95 duration-200">
                            <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6"><Lock size={36}/></div>
                            <h3 className="text-xl font-black mb-2 uppercase tracking-tight">Kunci Laporan?</h3>
                            <p className="text-[10px] text-slate-500 mb-8 font-bold uppercase tracking-wide px-4">Data akan dibekukan permanen untuk menjaga integritas harian.</p>
                            <div className="flex gap-3">
                                <button onClick={() => setShowLockConfirm(false)} className="flex-1 py-4 bg-slate-100 rounded-2xl font-black text-[10px] uppercase tracking-widest text-slate-500">Batal</button>
                                <button onClick={handleConfirmLock} className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-blue-500/30">Ya, Kunci!</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default RekapanDashboard;